import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Autocomplete,
  Divider,
  Alert,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import {
  ROLE_DEFINITIONS,
  ROLE_TYPES,
  RELATIONSHIP_OPTIONS,
  getRolesForDocumentTypes,
  getRoleTypesForDocumentTypes,
  getRoleDefinition,
  generatePlaceholdersForRole,
  generatePlaceholdersForCoFiduciaries,
  getRoleRequirements,
} from '../config/roleDefinitions';

// Default empty person structure
const createEmptyPerson = (id) => ({
  id,
  name: '',
  relationship: '',
  streetAddress: '',
  cityStateZip: '',
  phone: '',
  assignedRoles: [],
});

/**
 * KeyPeopleManager Component
 *
 * Allows users to define key people in their estate plan and assign roles to them.
 * Automatically generates placeholder values based on role assignments.
 */
const KeyPeopleManager = ({
  documentTypes: documentTypesProp,
  documentType,               // Accept singular for backward compat
  existingPlaceholders = {},
  placeholders = {},  // All placeholders from the document
  hasMinorChildren = true,    // When false, hide guardian roles
  successorLevels = 3,        // How many levels of successors to show (1, 2, or 3)
  onPlaceholdersGenerated,
  onBack,
}) => {
  // Coerce to array: prefer documentTypes, fall back to [documentType]
  const documentTypes = documentTypesProp?.length
    ? documentTypesProp
    : (documentType ? [documentType] : []);
  // State for managing people
  const [people, setPeople] = useState([createEmptyPerson(1)]);
  const [nextId, setNextId] = useState(2);

  // State for remaining field values (non-role placeholders)
  const [remainingFieldValues, setRemainingFieldValues] = useState({});

  // State for UI
  const [expandedPerson, setExpandedPerson] = useState(1);
  const [errors, setErrors] = useState([]);
  const [showAllRemainingFields, setShowAllRemainingFields] = useState(false);

  // Get available roles for all selected document types (aggregated)
  const availableRoles = useMemo(() => {
    const roleIds = getRolesForDocumentTypes(documentTypes);
    return roleIds.map(id => ({
      ...getRoleDefinition(id),
      id,
    })).filter(Boolean);
  }, [documentTypes]);

  // Get ALL role types for the document (unfiltered) — used to compute roleFilledKeys
  // so that filtered-out roles (guardian, 3rd-level) still suppress their fields
  const allRoleTypesUnfiltered = useMemo(() => {
    return getRoleTypesForDocumentTypes(documentTypes);
  }, [documentTypes]);

  // Get role types organized by function (Executor, Trustee, etc.) with positions
  // Filtered by hasMinorChildren and successorLevels for the UI
  const roleTypes = useMemo(() => {
    let types = getRoleTypesForDocumentTypes(documentTypes);
    if (!hasMinorChildren) {
      types = types.filter(rt => rt.id !== 'guardian');
    }
    if (successorLevels < 3) {
      types = types.map(rt => ({
        ...rt,
        positions: rt.positions.filter(pos => pos.position <= successorLevels),
      })).filter(rt => rt.positions.length > 0);
    }
    return types;
  }, [documentTypes, hasMinorChildren, successorLevels]);

  // Get all role IDs from role types (for validation)
  const allRoleIds = useMemo(() => {
    const ids = [];
    for (const roleType of roleTypes) {
      for (const pos of roleType.positions) {
        ids.push(pos.roleId);
      }
    }
    return ids;
  }, [roleTypes]);

  // Calculate remaining placeholders (not covered by role assignments)
  // Uses allRoleTypesUnfiltered so that hidden roles (guardian when no minor children,
  // 3rd-level successors when levels < 3) still suppress their fields from "Additional Info"
  const remainingPlaceholders = useMemo(() => {
    // Get all placeholder keys that would be filled by roles (unfiltered)
    const roleFilledKeys = new Set();
    for (const roleType of allRoleTypesUnfiltered) {
      for (const pos of roleType.positions) {
        const role = getRoleDefinition(pos.roleId);
        if (role?.placeholderFields) {
          role.placeholderFields.forEach(f => roleFilledKeys.add(f.toLowerCase()));
        }
        if (role?.aliasFields) {
          role.aliasFields.forEach(f => roleFilledKeys.add(f.toLowerCase()));
        }
      }
    }

    // Return placeholders not covered by roles
    return Object.entries(placeholders)
      .filter(([key]) => {
        const cleanKey = key.replace(/^\{\{|\}\}$/g, '').toLowerCase();
        return !roleFilledKeys.has(cleanKey);
      })
      .map(([key, info]) => ({
        key: key.replace(/^\{\{|\}\}$/g, ''),
        description: info?.description || ''
      }));
  }, [placeholders, allRoleTypesUnfiltered]);

  // Count filled remaining fields
  const filledRemainingCount = Object.values(remainingFieldValues).filter(v => v && v.trim() !== '').length;

  // Calculate which fields are required based on assigned roles
  const getRequiredFieldsForPerson = (person) => {
    let requiresAddress = false;
    let requiresPhone = false;
    let requiresRelationship = false;

    for (const roleId of person.assignedRoles) {
      const reqs = getRoleRequirements(roleId);
      if (reqs.requiresAddress) requiresAddress = true;
      if (reqs.requiresPhone) requiresPhone = true;
      if (reqs.requiresRelationship) requiresRelationship = true;
    }

    return { requiresAddress, requiresPhone, requiresRelationship };
  };

  // Add a new person
  const handleAddPerson = () => {
    const newPerson = createEmptyPerson(nextId);
    setPeople([...people, newPerson]);
    setExpandedPerson(nextId);
    setNextId(nextId + 1);
  };

  // Remove a person
  const handleRemovePerson = (personId) => {
    if (people.length <= 1) return; // Keep at least one person
    setPeople(people.filter(p => p.id !== personId));
    if (expandedPerson === personId) {
      setExpandedPerson(people.find(p => p.id !== personId)?.id || null);
    }
  };

  // Update a person's field
  const handleUpdatePerson = (personId, field, value) => {
    setPeople(people.map(p =>
      p.id === personId ? { ...p, [field]: value } : p
    ));
  };

  // Toggle a role assignment for a person
  const handleToggleRole = (personId, roleId) => {
    setPeople(prevPeople => {
      // Check if the target person already has this role
      const targetPerson = prevPeople.find(p => p.id === personId);
      const hasRole = targetPerson?.assignedRoles.includes(roleId);

      return prevPeople.map(p => {
        if (p.id === personId) {
          // Toggle the role for the target person
          if (hasRole) {
            // Remove the role
            return { ...p, assignedRoles: p.assignedRoles.filter(r => r !== roleId) };
          } else {
            // Add the role
            return { ...p, assignedRoles: [...p.assignedRoles, roleId] };
          }
        } else {
          // NEW: Check if this role allows co-fiduciaries
          const roleDefinition = ROLE_DEFINITIONS[roleId];
          const allowsCoFiduciary = roleDefinition?.allowsCoFiduciary ?? false;

          if (!allowsCoFiduciary) {
            // For roles that don't allow co-fiduciaries: remove from other people
            if (!hasRole && p.assignedRoles.includes(roleId)) {
              return { ...p, assignedRoles: p.assignedRoles.filter(r => r !== roleId) };
            }
          }
          // For roles that allow co-fiduciaries: do nothing (keep existing assignments)
          return p;
        }
      });
    });
  };

  // Check if a role is assigned to someone
  const isRoleAssigned = (roleId) => {
    return people.some(p => p.assignedRoles.includes(roleId));
  };

  // Get who a role is assigned to
  const getRoleAssignment = (roleId) => {
    return people.find(p => p.assignedRoles.includes(roleId));
  };

  // Validate all entries
  const validateEntries = () => {
    const validationErrors = [];

    // Check each person
    for (const person of people) {
      if (person.assignedRoles.length === 0) continue; // Skip people with no roles

      const reqs = getRequiredFieldsForPerson(person);

      if (!person.name.trim()) {
        validationErrors.push(`Please enter a name for person #${person.id}`);
      }

      if (reqs.requiresRelationship && !person.relationship.trim()) {
        validationErrors.push(`${person.name || `Person #${person.id}`} needs a relationship specified for their role(s)`);
      }

      if (reqs.requiresAddress && !person.streetAddress.trim()) {
        validationErrors.push(`${person.name || `Person #${person.id}`} needs an address for their Healthcare Agent role`);
      }

      if (reqs.requiresAddress && !person.cityStateZip.trim()) {
        validationErrors.push(`${person.name || `Person #${person.id}`} needs city/state/zip for their Healthcare Agent role`);
      }

      if (reqs.requiresPhone && !person.phone.trim()) {
        validationErrors.push(`${person.name || `Person #${person.id}`} needs a phone number for their Healthcare Agent role`);
      }
    }

    // Check for co-fiduciary limits
    for (const roleType of roleTypes) {
      for (const pos of roleType.positions) {
        const roleDefinition = ROLE_DEFINITIONS[pos.roleId];
        const assignedPeople = people.filter(p => p.assignedRoles.includes(pos.roleId));

        if (assignedPeople.length > 1 && !roleDefinition?.allowsCoFiduciary) {
          validationErrors.push(
            `${pos.label} ${roleType.label} does not allow multiple people`
          );
        }

        if (roleDefinition?.maxCoFiduciaries &&
            assignedPeople.length > roleDefinition.maxCoFiduciaries) {
          validationErrors.push(
            `${pos.label} ${roleType.label} allows maximum ${roleDefinition.maxCoFiduciaries} people`
          );
        }
      }
    }

    // Note: Role assignments are now optional - users can proceed with partial assignments
    // The remaining fields section allows filling in any missing data manually

    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  // Generate all placeholder values
  const generatePlaceholderValues = () => {
    const allPlaceholders = {};

    // Group people by their assigned roles to detect co-fiduciaries
    const roleAssignments = {}; // Map: roleId -> [person1, person2, ...]

    for (const person of people) {
      for (const roleId of person.assignedRoles) {
        if (!roleAssignments[roleId]) {
          roleAssignments[roleId] = [];
        }
        roleAssignments[roleId].push(person);
      }
    }

    // Generate placeholders for each role (handles both single and multi-person)
    for (const [roleId, assignedPeople] of Object.entries(roleAssignments)) {
      const rolePlaceholders = generatePlaceholdersForCoFiduciaries(
        assignedPeople,
        roleId
      );
      Object.assign(allPlaceholders, rolePlaceholders);
    }

    // Add remaining field values (non-role placeholders)
    Object.entries(remainingFieldValues).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        allPlaceholders[key] = value;
      }
    });

    return allPlaceholders;
  };

  // Handle completion
  const handleComplete = () => {
    if (!validateEntries()) {
      return;
    }

    // DEBUG: Log role assignments per person before generating placeholders
    console.log('🔍 [KeyPeopleManager] People and their roles:');
    people.forEach(p => {
      if (p.assignedRoles.length > 0) {
        console.log(`  Person "${p.name}" (${p.relationship}): roles = [${p.assignedRoles.join(', ')}]`);
      }
    });

    const placeholders = generatePlaceholderValues();

    onPlaceholdersGenerated(placeholders, people);
  };

  // Count assigned roles for a person
  const getAssignedRoleCount = (person) => {
    return person.assignedRoles.length;
  };

  // Render a single person card
  const renderPersonCard = (person) => {
    const reqs = getRequiredFieldsForPerson(person);
    const roleCount = getAssignedRoleCount(person);
    const isExpanded = expandedPerson === person.id;

    return (
      <Accordion
        key={person.id}
        expanded={isExpanded}
        onChange={() => setExpandedPerson(isExpanded ? null : person.id)}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
            <PersonIcon color={person.name ? 'primary' : 'disabled'} />
            <Typography sx={{ flexGrow: 1 }}>
              {person.name || `Person #${person.id}`}
              {person.relationship && (
                <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                  ({person.relationship})
                </Typography>
              )}
            </Typography>
            {roleCount > 0 && (
              <Chip
                label={`${roleCount} role${roleCount > 1 ? 's' : ''}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {people.length > 1 && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemovePerson(person.id);
                }}
                sx={{ ml: 1 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Basic Info */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={person.name}
                onChange={(e) => handleUpdatePerson(person.id, 'name', e.target.value)}
                required
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={RELATIONSHIP_OPTIONS}
                value={person.relationship}
                onChange={(e, newValue) => handleUpdatePerson(person.id, 'relationship', newValue || '')}
                onInputChange={(e, newValue) => handleUpdatePerson(person.id, 'relationship', newValue || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Relationship to Client"
                    required={reqs.requiresRelationship}
                    size="small"
                    placeholder="Select or type custom"
                    helperText={reqs.requiresRelationship ? 'Required - select or type custom' : 'Select or type custom'}
                  />
                )}
              />
            </Grid>

            {/* Address fields - shown if any role requires them */}
            {(reqs.requiresAddress || person.streetAddress || person.cityStateZip) && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Chip label="Contact Information (for Healthcare Agent)" size="small" />
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    value={person.streetAddress}
                    onChange={(e) => handleUpdatePerson(person.id, 'streetAddress', e.target.value)}
                    required={reqs.requiresAddress}
                    size="small"
                    placeholder="123 Main Street"
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="City, State, ZIP"
                    value={person.cityStateZip}
                    onChange={(e) => handleUpdatePerson(person.id, 'cityStateZip', e.target.value)}
                    required={reqs.requiresAddress}
                    size="small"
                    placeholder="Los Angeles, CA 90001"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={person.phone}
                    onChange={(e) => handleUpdatePerson(person.id, 'phone', e.target.value)}
                    required={reqs.requiresPhone}
                    size="small"
                    placeholder="(555) 123-4567"
                  />
                </Grid>
              </>
            )}

            {/* Role assignments */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Assign Roles" size="small" color="primary" />
              </Divider>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select positions for each role type. Click a position chip to assign it to this person.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2, p: 1, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                <InfoIcon sx={{ fontSize: 16, color: 'info.main', mt: 0.25 }} />
                <Typography variant="caption" color="text.secondary">
                  <strong>Co-fiduciaries:</strong> To add co-fiduciaries (e.g., co-executors), assign the same role to multiple people. Add another person using the button below, then select the same position for them.
                </Typography>
              </Box>

              {roleTypes.map(roleType => (
                <Box key={roleType.id} sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ minWidth: 140 }}>
                      {roleType.label}:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {roleType.positions.map(pos => {
                        const isAssignedToThis = person.assignedRoles.includes(pos.roleId);
                        const assignedTo = getRoleAssignment(pos.roleId);
                        const isAssignedToOther = assignedTo && assignedTo.id !== person.id;
                        const allAssignedPeople = people.filter(p => p.assignedRoles.includes(pos.roleId));
                        const isMultiAssigned = allAssignedPeople.length > 1;
                        const roleDefinition = ROLE_DEFINITIONS[pos.roleId];
                        const allowsCoFiduciary = roleDefinition?.allowsCoFiduciary ?? false;

                        return (
                          <Tooltip
                            key={pos.roleId}
                            title={
                              isAssignedToOther
                                ? `Assigned to ${assignedTo.name || `Person #${assignedTo.id}`}`
                                : `Assign as ${pos.label} ${roleType.label}`
                            }
                          >
                            <Chip
                              label={
                                isMultiAssigned && allowsCoFiduciary
                                  ? `${pos.label} (${allAssignedPeople.length} people)`
                                  : isAssignedToOther
                                  ? `${pos.label} (${assignedTo.name?.split(' ')[0] || '?'})`
                                  : pos.label
                              }
                              onClick={() => handleToggleRole(person.id, pos.roleId)}
                              color={isAssignedToThis ? 'primary' : 'default'}
                              variant={isAssignedToThis ? 'filled' : 'outlined'}
                              icon={
                                isMultiAssigned && allowsCoFiduciary && isAssignedToThis
                                  ? <GroupIcon fontSize="small" />
                                  : isAssignedToThis ? <CheckIcon /> : undefined
                              }
                              size="small"
                              sx={{
                                opacity: isAssignedToOther ? 0.6 : 1,
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 1,
                                },
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  </Box>
                </Box>
              ))}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  // Render role summary - organized by role type
  const renderRoleSummary = () => {
    // Count total positions and assigned positions
    let totalPositions = 0;
    let assignedPositions = 0;

    for (const roleType of roleTypes) {
      for (const pos of roleType.positions) {
        totalPositions++;
        if (isRoleAssigned(pos.roleId)) {
          assignedPositions++;
        }
      }
    }

    return (
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2">
            Role Assignment Summary
          </Typography>
          <Chip
            label={`${assignedPositions} of ${totalPositions} assigned`}
            size="small"
            color={assignedPositions === totalPositions ? 'success' : 'warning'}
          />
        </Box>

        {/* Show each role type with its assignments */}
        <Grid container spacing={1}>
          {roleTypes.map(roleType => (
            <Grid item xs={12} sm={6} md={4} key={roleType.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 100 }}>
                  {roleType.label}:
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {roleType.positions.map(pos => {
                    const assignedPerson = getRoleAssignment(pos.roleId);
                    return (
                      <Chip
                        key={pos.roleId}
                        label={assignedPerson ? assignedPerson.name?.split(' ')[0] || '?' : pos.label}
                        size="small"
                        color={assignedPerson ? 'success' : 'default'}
                        variant={assignedPerson ? 'filled' : 'outlined'}
                        sx={{
                          height: 22,
                          fontSize: '0.75rem',
                          opacity: assignedPerson ? 1 : 0.5,
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Key People in Your Estate Plan
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Add the key people in your estate plan and assign their roles. Each person&apos;s
        information will automatically fill in the appropriate fields throughout your
        documents.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Documents:</strong> {documentTypes.join(', ') || 'None selected'}
          <br />
          <strong>Role Types:</strong> {roleTypes.length} ({roleTypes.map(r => r.label).join(', ')})
        </Typography>
      </Alert>

      {/* Co-Fiduciary Support Info */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Co-Fiduciary Support:</strong> You can assign multiple people to the same role
          for positions like Executor, Trustee, and Guardian. Simply click the same role on
          multiple people&apos;s cards. Their names will be automatically combined in documents
          (e.g., &quot;John Smith and Jane Doe&quot;).
        </Typography>
      </Alert>

      {/* Role Summary */}
      {renderRoleSummary()}

      {/* People List */}
      {people.map(person => renderPersonCard(person))}

      {/* Add Person Button */}
      <Button
        startIcon={<AddIcon />}
        onClick={handleAddPerson}
        variant="outlined"
        sx={{ mb: 3 }}
      >
        Add Another Person
      </Button>

      {/* Remaining Fields Section */}
      {remainingPlaceholders.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon color="secondary" />
              <Typography variant="h6">
                Additional Information
              </Typography>
            </Box>
            <Chip
              label={`${filledRemainingCount} of ${remainingPlaceholders.length} filled`}
              size="small"
              color={filledRemainingCount === remainingPlaceholders.length ? 'success' : 'default'}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These fields are not covered by role assignments and need to be filled in manually:
          </Typography>

          <Grid container spacing={2}>
            {/* Show first 6 fields always */}
            {remainingPlaceholders.slice(0, 6).map(item => (
              <Grid item xs={12} sm={6} key={item.key}>
                <TextField
                  label={item.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  placeholder={item.description || `Enter ${item.key.replace(/_/g, ' ')}`}
                  value={remainingFieldValues[item.key] || ''}
                  onChange={(e) => setRemainingFieldValues(prev => ({
                    ...prev,
                    [item.key]: e.target.value
                  }))}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Collapsible section for remaining fields */}
          {remainingPlaceholders.length > 6 && (
            <>
              <Collapse in={showAllRemainingFields}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {remainingPlaceholders.slice(6).map(item => (
                    <Grid item xs={12} sm={6} key={item.key}>
                      <TextField
                        label={item.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        placeholder={item.description || `Enter ${item.key.replace(/_/g, ' ')}`}
                        value={remainingFieldValues[item.key] || ''}
                        onChange={(e) => setRemainingFieldValues(prev => ({
                          ...prev,
                          [item.key]: e.target.value
                        }))}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Collapse>

              <Button
                onClick={() => setShowAllRemainingFields(!showAllRemainingFields)}
                startIcon={showAllRemainingFields ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ mt: 2 }}
                size="small"
              >
                {showAllRemainingFields
                  ? 'Show fewer fields'
                  : `Show ${remainingPlaceholders.length - 6} more fields`}
              </Button>
            </>
          )}
        </Paper>
      )}

      {/* Validation Errors */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Please fix the following issues:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        {onBack && (
          <Button onClick={onBack} size="large">
            Back
          </Button>
        )}
        <Button
          variant="contained"
          size="large"
          onClick={handleComplete}
          endIcon={<CheckIcon />}
        >
          Generate Document Data
        </Button>
      </Box>
    </Box>
  );
};

export default KeyPeopleManager;
